import { getUserPresentableMessage } from "@core/foundation/error";
import type { LoggerContract } from "@core/foundation/logger";
import type { Promisable } from "@core/foundation/promise";
import { isNullish } from "@core/foundation/types";
import {
  // oxlint-disable-next-line custom/restrict-named-imports
  FetchApiClient,
} from "@core/ports/http";

import { githubApi } from "@apis/account/api";

import { env } from "../../../bootstrap/env.ts";
import { router } from "../../../bootstrap/router.ts";
import { connectOnReturnStore } from "../state/connect-on-return.ts";
import { connectSessionStore } from "../state/connect-session.ts";

interface GithubConnectOnReturnConfig {
  readonly getToken: () => null | string;
  readonly logger: LoggerContract;
}

export function createGithubConnectOnReturnHandler({
  getToken,
  logger,
}: GithubConnectOnReturnConfig): (url: string) => Promisable<void> {
  return async (url: string): Promise<void> => {
    const { pathname, searchParams } = new URL(url);

    if (
      pathname !== router.getUrl("GithubConnectionsCallback") ||
      connectOnReturnStore.isPending
    ) {
      return;
    }

    const code = searchParams.get("code");

    const installationId = searchParams.get("installation_id");

    const session = searchParams.get("state");

    const token = getToken();

    if (
      isNullish(code) ||
      isNullish(installationId) ||
      isNullish(session) ||
      isNullish(token)
    ) {
      return;
    }

    if (isNullish(connectSessionStore.session)) {
      logger.warn("Session not found");
      connectOnReturnStore.setError("Session not found");

      return;
    }

    if (session !== connectSessionStore.session) {
      logger.warn("Session mismatch");
      connectOnReturnStore.setError("Session mismatch");

      return;
    }

    if (connectSessionStore.isCodeRedeemed(code)) {
      logger.warn("Code already redeemed");
      connectOnReturnStore.setError("Code already redeemed");

      return;
    }

    connectOnReturnStore.setIsPending(true);

    try {
      const api = new FetchApiClient({
        api: githubApi,
        getToken: () => token,
        stage: env.STAGE,
      }).getApi();

      await api.completeInstall({
        code,
        installationId: Number(installationId),
      });

      connectSessionStore.redeemCode(code);
      connectSessionStore.endSession();
    } catch (error) {
      logger.error("Failed to complete GitHub connection", error);

      connectOnReturnStore.setError(
        getUserPresentableMessage(
          error,
          "Failed to complete the GitHub connection.",
        ),
      );
    } finally {
      connectOnReturnStore.setIsPending(false);
    }
  };
}
